import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showLoader } from "../../redux/slices/loaderSlice";
import { downloadAgsReport } from "../../redux/slices/mapSlice";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import {
  MapLayoutWidth,
  RadiusReportEnum,
  RadiusTypeEnum,
} from "../../utils/helper";
import { palette, themeWidth } from "../../themes/theme";
import ClearIcon from "@mui/icons-material/Clear";
import { generateDemographicReport } from "../../api";
import JSZip from "jszip";
import Papa from "papaparse";
import { CustomReportClass } from "./utilities/CustomReport";

export const AgsReport = ({ close, mapDraw, mapRef, markerRef }) => {
  return (
    <>
      <AgsReportList
        mapRef={mapRef}
        close={close}
        mapDraw={mapDraw}
        markerRef={markerRef}
      />
    </>
  );
};

export const AgsReportList = (props) => {
  const activeAgsReport = useSelector((state) =>
    state.map.tradeAreas.find((d) => d.id === state.map.showAgsReport)
  );
  const agsReportList = useSelector((state) => state.map.agsReportList);
  // user.adminData.userSubscriptionPlanReferenceName === "professional"
  const [format, setFormat] = useState("pdf");
  const [country, setCountry] = useState("USA");
  const [selectedReport, setSelectedReport] = useState([]);
  const dispatch = useDispatch();
  const [error, setError] = useState("");

  // Synthetic picker entry for the new consolidated "Demographics Report".
  // The UI-only referenceName "demographics_report" never reaches the AGS
  // API — submit-time it's translated to cr_xp5 (and deduped against an
  // existing cr_xp5 selection) so we only ask AGS for the demographics
  // CSV once even when both the legacy and new options are picked.
  const DEMOGRAPHICS_REPORT_REFERENCE = "demographics_report";
  const demographicsReportSyntheticEntry = {
    id: "synthetic-demographics-report",
    name: "Demographics Report",
    referenceName: DEMOGRAPHICS_REPORT_REFERENCE,
    status: true,
  };
  // Legacy demographic reports replaced by the consolidated "Demographics
  // Report" synthetic entry above. Hidden from the picker per
  // location-demogr/FEATURE.md (step 5). This is display-only — the
  // ags_report_types rows, the CustomReport renderer branches, and the API
  // helpers all stay intact, so saved layouts and direct API calls that
  // reference these codes keep working.
  const HIDDEN_LEGACY_REPORTS = ["cr_xp1", "cr_xp3", "cr_xp5"];
  const augmentedAgsReportList = [
    demographicsReportSyntheticEntry,
    ...agsReportList.filter(
      (d) => !HIDDEN_LEGACY_REPORTS.includes(d.referenceName)
    ),
  ];

  const generatePDFReport = async (fileData, type) => {
    const zip = new JSZip();
    try {
      const zipContent = await zip.loadAsync(fileData);
      const result = {};

      // Iterate over each file in the zip
      for (const filename of Object.keys(zipContent.files)) {
        if (filename.endsWith(".csv")) {
          const csvFile = zipContent.files[filename];
          const csvContent = await csvFile.async("text");

          // Parse CSV to JSON
          const parsed = Papa.parse(csvContent, {
            header: true,
            skipEmptyLines: true,
          });
          const newFileName = filename
            .replace(/[^a-zA-Z0-9]/g, "_")
            .replace(/_+/g, "_")
            .toLowerCase();
          // Detect Canada reports first — their filenames ("Canada Crime",
          // "Canada Demographics 1") also contain "crime"/"demographics_1",
          // so they must be matched before the generic US checks.
          let key = newFileName.includes("canada_crime")
            ? "canada_crime"
            : newFileName.includes("canada_demographics_1")
            ? "canada_demographics_1"
            : newFileName.includes("crime")
            ? "crime"
            : newFileName.includes("demographics_5")
            ? "demographics_5"
            : newFileName.includes("demographics_3")
            ? "demographics_3"
            : newFileName.includes("demographics_1")
            ? "demographics_1"
            : newFileName.includes("environment")
            ? "environment"
            : "undefined";

          // Fallback for older/unnamed CSVs (e.g. "undefined.csv"): infer
          // Canada report by row count.
          if (key === "undefined") {
            key =
              parsed.data.length < 30
                ? "canada_crime"
                : "canada_demographics_1";
          }
          result[key] = parsed.data;
        }
      }

      if (type === RadiusReportEnum.RING_REPORT) {
        await CustomReportClass.convertRadiusRingToCrimeRiskPdf({
          activeMarker: activeAgsReport,
          mapRef: props.mapRef,
          markerRef: props.markerRef,
          csvData: result,
          country,
          selectedReports: selectedReport,
        });
      } else if (type === RadiusReportEnum.POLYGON_REPORT) {
        await CustomReportClass.convertPolygonToCrimeRiskPdf({
          activeMarker: activeAgsReport,
          mapRef: props.mapRef,
          markerRef: props.markerRef,
          csvData: result,
          country,
          selectedReports: selectedReport,
        });
      }
      // setJsonData(result);
      console.log("Parsed JSON Data:", result);
    } catch (err) {
      console.error("Error extracting zip file:", err);
    }
  };

  const downLoadReport = async () => {
    setError("");
    if (
      !(
        activeAgsReport?.radiusSetting.filter(
          (item) => parseFloat(item.defaultRadius) > 0 && item.type !== ""
        ).length > 0
      ) &&
      activeAgsReport?.type === "trade-area"
    ) {
      setError(
        "Please select any type and that type value should be greater than 0"
      );
    } else if (selectedReport.length === 0) {
      setError("Please select report type");
    } else {
      dispatch(showLoader({ showLoader: true }));
      dispatch(downloadAgsReport(true));
      // Translate the synthetic UI-only reference to cr_xp5 (the AGS code
      // its renderer reads from), dedup against a possible cr_xp5 already
      // in the selection so the API isn't asked twice for the same CSV.
      const apiReportTypes = Array.from(
        new Set(
          selectedReport.map((r) =>
            r === DEMOGRAPHICS_REPORT_REFERENCE ? "cr_xp5" : r
          )
        )
      );
      let body = {
        reportFormat: "file",
        reportTypes: apiReportTypes,
      };
      if (activeAgsReport?.type === "trade-area") {
        const report = activeAgsReport?.radiusSetting
          .filter((item) => parseFloat(item.defaultRadius) > 0)
          .map((item) =>
            item.type === RadiusTypeEnum.RADIUS_RING
              ? RadiusReportEnum.RING_REPORT
              : item.type === RadiusTypeEnum.DRIVE_TIME
              ? RadiusReportEnum.DRIVE_TIME_REPORT
              : ""
          );
        body.lat = activeAgsReport?.latlng.lat;
        body.lng = activeAgsReport?.latlng.lng;
        body.reportName = Array.from(new Set(report)).filter((d) => d);
        if (report?.includes(RadiusReportEnum.DRIVE_TIME_REPORT))
          body.driveTime = activeAgsReport?.radiusSetting
            .filter(
              (item) =>
                item.type === RadiusTypeEnum.DRIVE_TIME &&
                parseFloat(item.defaultRadius) > 0
            )
            .map((item) => parseFloat(item.defaultRadius));
        if (report?.includes(RadiusReportEnum.RING_REPORT))
          body.radius = activeAgsReport?.radiusSetting
            .filter(
              (item) =>
                item.type === RadiusTypeEnum.RADIUS_RING &&
                parseFloat(item.defaultRadius) > 0
            )
            .map((item) => parseFloat(item.defaultRadius));
      } else {
        body.reportName = [RadiusReportEnum.POLYGON_REPORT];
        body.label = activeAgsReport?.name;
        const polygonInfo = props.mapDraw?.get(activeAgsReport?.id);
        body.polygonCoords = JSON.parse(
          JSON.stringify(polygonInfo?.geometry.coordinates)
        ).shift();
      }
      try {
        body.country = country.toLowerCase();
        const res = await generateDemographicReport({ body });
        if (format === "pdf") {
          await generatePDFReport(res.data, body.reportName[0]);
        } else {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "reports.zip"); // Filename for the downloaded file
          document.body.appendChild(link);
          link.click();
          link.remove(); // Clean up the DOM
          // Optional: Clean up the URL object after a certain period
          setTimeout(() => window.URL.revokeObjectURL(url), 100);
        }
        props.close();
      } catch (e) {
        console.error("Error downloading the report:", e);
      } finally {
        dispatch(showLoader({ showLoader: false }));
        dispatch(downloadAgsReport(false));
      }
    }
  };

  const addReport = (report) => {
    setSelectedReport((prevReports) => {
      if (prevReports.includes(report)) {
        return prevReports.filter((r) => r !== report);
      } else {
        return [...prevReports, report];
      }
    });
  };

  useEffect(() => {
    const lat = activeAgsReport.latlng.lat;
    const lng = activeAgsReport.latlng.lng;
    (async () => {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`
      );
      const data = await response.json();
      const countryCode = data.address?.country_code?.toUpperCase();

      if (countryCode === "US") setCountry("USA");
      if (countryCode === "CA") setCountry("CAN");
    })();
  }, []);

  return (
    <>
      <Box
        sx={{
          height: 350,
          width: 300,
          background: palette.background.light,
          position: "absolute",
          left: themeWidth[MapLayoutWidth] + 25,
          bottom: 25,
          borderRadius: 2,
          boxShadow: "1px 3px 12px black",
          p: "4px 12px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          color: palette.text.dark,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: "8px",
          }}
        >
          <Typography fontSize={14} sx={{ fontWeight: "bold" }}>
            Demographic Reports
          </Typography>
          <IconButton sx={{ p: 0 }} onClick={props.close}>
            <ClearIcon fontSize="medium" color="primary" />
          </IconButton>
        </Box>
        <Box
          sx={{
            height: "50%",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {augmentedAgsReportList.length > 0 ? (
            augmentedAgsReportList.map((d, i) => (
              <FormControlLabel
                key={i}
                control={
                  <Checkbox
                    onChange={() => addReport(d.referenceName)}
                    checked={selectedReport.includes(d.referenceName)}
                  />
                }
                sx={{
                  "& .MuiCheckbox-root": {
                    padding: "2px 8px",
                  },
                  "& .MuiFormControlLabel-label": {
                    fontSize: 12,
                  },
                }}
                label={
                  d.name === "Demographics 1"
                    ? "Demographic Overview (old)"
                    : d.name === "Demographics 3"
                    ? "Demographic Essentials (old)"
                    : d.name === "Demographics 5"
                    ? "Demographic Complete (old)"
                    : d.name
                }
                disabled={
                  d.referenceName.includes("can_")
                    ? "CAN" !== country
                    : "USA" !== country
                }
              />
            ))
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography>No records</Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ py: 1 }}>
          <Divider />
          <FormControl sx={{ py: 1 }}>
            <FormLabel sx={{ color: palette.text.dark }}>
              Select format
            </FormLabel>
            <RadioGroup
              value={format}
              onChange={(_, val) => setFormat(val)}
              row
            >
              <FormControlLabel
                value="pdf"
                control={<Radio size="small" color="primary" />}
                label="PDF"
              />
              <FormControlLabel
                value="file"
                control={<Radio size="small" color="primary" />}
                label="CSV"
              />
            </RadioGroup>
          </FormControl>
          {error && (
            <Typography
              sx={{
                color: palette.text.danger,
                fontSize: 12,
                textAlign: "center",
              }}
            >
              {error}
            </Typography>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Button
              variant="outlined"
              sx={{ fontSize: 12, fontWeight: "bold" }}
              disabled={selectedReport.length === 0}
              onClick={downLoadReport}
            >
              Download
            </Button>
            <Button
              variant="outlined"
              sx={{ fontSize: 12, fontWeight: "bold" }}
              onClick={props.close}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
};
